import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WEB_SOCKET_URL } from '../../assets/constants'
import { WebSocketResponseMessage, WebSocketResponseMessage_CandleSubscriptionResponse, WebSocketResponseMessage_OrderUpdate } from '../../proto/generated/websocket'
import { useSelector } from 'react-redux'
import { selectIsEmbedded } from '../../selectors/device'
import { isEmbeddedStrategyChartContext } from '../../util/embeddedChartContext'

export interface WebSocketContextValue {
    socket: WebSocket | null
    sendMessage: (message: Uint8Array) => void
    addListener: (listener: (data: Uint8Array) => void) => void
    removeListener: (listener: (data: Uint8Array) => void) => void
    addOpenListener: (listener: () => void) => void
    removeOpenListener: (listener: () => void) => void
}

interface WebSocketProviderProps {
    children: ReactNode
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export { WebSocketContext }

const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
    const socketRef = useRef<WebSocket | null>(null)
    const [socket, setSocket] = useState<WebSocket | null>(null)
    const listeners = useRef<((data: Uint8Array) => void)[]>([])
    const openListeners = useRef<(() => void)[]>([])
    const messageQueue = useRef<Uint8Array[]>([])
    const isEmbeded = useSelector(selectIsEmbedded)

    const notifyOpenListeners = useCallback(() => {
        openListeners.current.forEach((listener) => listener())
    }, [])

    const flushMessageQueue = useCallback((currentSocket: WebSocket) => {
        while (messageQueue.current.length > 0) {
            const message = messageQueue.current.shift()
            if (message) {
                currentSocket.send(message)
            }
        }
    }, [])

    const init = useCallback((): WebSocket => {
        if (!socketRef.current) {
            const newSocket = new WebSocket(WEB_SOCKET_URL)
            newSocket.binaryType = 'arraybuffer'
            ;(window as any).getWebSocket = newSocket

            newSocket.onmessage = (msg) => {
                const data = new Uint8Array(msg.data as ArrayBuffer)
                listeners.current.forEach((callback) => callback(data))
            }

            newSocket.onopen = () => {
                flushMessageQueue(newSocket)
                notifyOpenListeners()
            }

            newSocket.onclose = (ev) => {
                console.log('socket closed', ev, JSON.stringify(ev))
                messageQueue.current = []
                socketRef.current = null
                setSocket(null)
                setTimeout(init, 2000)
            }

            newSocket.onerror = () => {
                newSocket.close()
            }

            socketRef.current = newSocket
            setSocket(newSocket)
        }

        return socketRef.current as WebSocket
    }, [flushMessageQueue, notifyOpenListeners])

    const sendMessage = useCallback(
        (message: Uint8Array) => {
            if (isEmbeded) return

            const currentSocket = init()

            if (currentSocket.readyState === WebSocket.OPEN) {
                currentSocket.send(message)
            } else {
                messageQueue.current.push(message)
            }
        },
        [isEmbeded, init]
    )

    useEffect(() => {
        if (!isEmbeded) return
        const chartBridge = isEmbeddedStrategyChartContext()
            ? window.strategyChartManager
            : window.chartManager
        if (!chartBridge) return
        chartBridge._publishToWebsocketManager = (candle: WebSocketResponseMessage_CandleSubscriptionResponse) => {
            const socketMessage = WebSocketResponseMessage.create()
            socketMessage.candleSubscriptionResponses = [candle]
            const m = WebSocketResponseMessage.encode(socketMessage).finish()

            if (listeners.current.length) {
                listeners.current.forEach((callback) => callback(m))
            }
        }
        if (isEmbeded && window.tradeManager) {
            window.tradeManager._publishOrderUpdatesToWebsocketManager = (orderUpdate: WebSocketResponseMessage_OrderUpdate) => {
                const socketMessage = WebSocketResponseMessage.create()
                socketMessage.orderUpdate = orderUpdate
                const m = WebSocketResponseMessage.encode(socketMessage).finish()

                if (listeners.current.length) {
                    listeners.current.forEach((callback) => callback(m))
                }
            }
        }
    }, [isEmbeded])

    useEffect(() => {
        if (!isEmbeded) {
            init()
        }

        return () => {
            if (socketRef.current) {
                listeners.current = []
                openListeners.current = []
                messageQueue.current = []
                socketRef.current.close()
                socketRef.current = null
            }
        }
    }, [isEmbeded, init])

    const addListener = useCallback((listener: (data: Uint8Array) => void) => {
        listeners.current.push(listener)
    }, [])

    const removeListener = useCallback((listener: (data: Uint8Array) => void) => {
        listeners.current = listeners.current.filter((ls) => ls !== listener)
    }, [])

    const addOpenListener = useCallback((listener: () => void) => {
        openListeners.current.push(listener)
    }, [])

    const removeOpenListener = useCallback((listener: () => void) => {
        openListeners.current = openListeners.current.filter((ls) => ls !== listener)
    }, [])

    const contextValue = useMemo<WebSocketContextValue>(
        () => ({
            socket,
            sendMessage,
            addListener,
            removeListener,
            addOpenListener,
            removeOpenListener,
        }),
        [socket, sendMessage, addListener, removeListener, addOpenListener, removeOpenListener]
    )

    return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
}

export default WebSocketProvider
